import { FunctionComponent } from 'react';
import { useRouter } from 'next/router';
import {
  PhotographIcon,
  DocumentDownloadIcon,
  LocationMarkerIcon,
  GlobeIcon
} from '@heroicons/react/outline';
import ImagePreview from './ImagePreview';
import LinkButton from '../LinkButton';

type ResumeHeaderProps = {
  name: string;
  imageUrl: string;
  location: string;
  role: string;
  languages?: string;
  portfolio?: string;
  slug: string;
};

function convertLanguageString(languageString: string): string {
  return languageString.split(', ').join(' / ');
}

const ResumeHeader: FunctionComponent<ResumeHeaderProps> = ({
  name,
  imageUrl,
  location,
  languages,
  portfolio,
  role,
  slug
}) => {
  const router = useRouter();

  return (
    <>
      <div className="max-w-5xl px-4 mx-auto sm:px-6 lg:px-8">
        <div className="sm:relative sm:flex sm:justify-between">
          <div className="sm:gap-8 sm:flex sm:items-center">
            <ImagePreview
              src={imageUrl}
              alt={name}
              placeholder="/images/placeholder.jpg"
            />
            <div className="mt-6 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="flex-1 min-w-0 mt-6 sm:hidden 2xl:block">
                <h1 className="text-3xl font-bold truncate text-egg">{name}</h1>
                <div className="text-3xl font-normal opacity-60 font-atyp-light sm:max-w-sm">
                  {role}
                </div>
              </div>
            </div>
          </div>
          <div className="right-0 sm:absolute top-10">
            <div className="mt-4">
              {location && (
                <div className="flex items-center">
                  <LocationMarkerIcon
                    className="mr-2.5 ml-3 w-7 h-7"
                    aria-hidden="true"
                  />
                  <p className="text-xl capitalize">{location}</p>
                </div>
              )}
              {languages && (
                <div className="flex mt-2">
                  <GlobeIcon
                    className="mr-2.5 ml-3 w-7 h-7"
                    aria-hidden="true"
                  />
                  <p className="w-48 text-xl">
                    {convertLanguageString(languages)}
                  </p>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-4 mt-5 sm:w-56">
              {portfolio && (
                <LinkButton color="pink" href={portfolio}>
                  <PhotographIcon
                    className="mr-2.5 -ml-1 w-7 h-7"
                    aria-hidden="true"
                  />
                  <span className="text-xl">Portfolio</span>
                </LinkButton>
              )}
              <LinkButton
                href={`/api/export/docx/${slug}?locale=${router.locale}`}
              >
                <DocumentDownloadIcon
                  className="mr-2.5 -ml-1 w-7 h-7"
                  aria-hidden="true"
                />
                <span className="text-xl">Download docx</span>
              </LinkButton>
              <LinkButton
                href={`${process.env.NEXT_PUBLIC_CMS_BASEURL}/api/resumes/${slug}/export?locale=${router.locale}`}
              >
                <DocumentDownloadIcon
                  className="mr-2.5 -ml-1 w-7 h-7"
                  aria-hidden="true"
                />
                <span className="text-xl ">Download PDF</span>
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResumeHeader;
